import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/site/SEO";
import SectionHeading from "@/components/site/SectionHeading";
import FinalCTA from "@/components/site/FinalCTA";
import { ABOUT_BLOCKS, BRAND, STATS, IMAGES } from "@/data/content";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

export default function AboutPage({ onStartProject }) {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
    return (
        <div data-testid="page-about">
            <SEO
                title="About — Editorial Craft. Engineering Rigor. Operator Humility."
                description="IdeaClap pairs agentic AI systems with senior human judgement to build creative + growth engines that compound. Our mission, principles, and why now."
                path="/about"
                keywords="about IdeaClap, AI marketing company, creative engineering company, AI human judgement, marketing operating system"
            />
            <header className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
                <div className="absolute inset-0 hero-radial pointer-events-none" />
                <div className="relative mx-auto max-w-7xl container-px grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            eyebrow="About IdeaClap"
                            title={
                                <>
                                    Applauding{" "}
                                    <span className="italic text-[hsl(var(--text-muted))]">creative brains.</span>
                                    <br className="hidden md:block" />
                                    Engineering the systems around them.
                                </>
                            }
                            subtitle="IdeaClap is a creative + marketing intelligence platform. We pair supervised AI agents with a curated network of senior operators — building durable systems for brand, content, and growth."
                        />
                    </div>
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="relative aspect-square rounded-3xl border border-black/[0.08] bg-white overflow-hidden shadow-deep"
                        >
                            <img
                                src={IMAGES.aboutMood}
                                alt="Atmospheric editorial brand image"
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="eager"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(255,255,255,0) 55%, rgba(255,255,255,0.55) 100%)",
                                }}
                            />
                        </motion.div>
                    </div>
                </div>
            </header>

            <section className="mx-auto max-w-7xl container-px pb-12">
                <div
                    ref={ref}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
                >
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 8 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            className="rounded-2xl border border-black/[0.07] bg-white p-5 shadow-soft"
                        >
                            <div className="font-display text-3xl text-[hsl(var(--text))] num-tabular">
                                {inView ? (
                                    <CountUp end={s.value} duration={1.6} decimals={s.decimals || 0} />
                                ) : (
                                    0
                                )}
                                <span className="text-[hsl(var(--gold))]">{s.suffix}</span>
                            </div>
                            <div className="text-[12px] uppercase tracking-[0.18em] font-mono text-[hsl(var(--text-subtle))] mt-1">
                                {s.label}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-5xl container-px py-20 lg:py-28">
                <div className="flex flex-col gap-16">
                    {ABOUT_BLOCKS.map((b, i) => (
                        <motion.article
                            key={b.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.55, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10"
                        >
                            <div className="md:col-span-3">
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    {b.eyebrow}
                                </div>
                            </div>
                            <div className="md:col-span-9">
                                <h3 className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-[-0.02em] text-[hsl(var(--text))]">
                                    {b.title}
                                </h3>
                                <p className="mt-5 text-[16px] leading-relaxed text-[hsl(var(--text-muted))] max-w-[68ch]">
                                    {b.body}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </section>

            <section className="relative py-20 lg:py-28 border-t border-black/[0.06] bg-[hsl(var(--bg-2))]/60">
                <div className="mx-auto max-w-5xl container-px">
                    <div className="rounded-3xl border border-black/[0.08] bg-white p-8 sm:p-12 relative overflow-hidden shadow-soft">
                        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[rgba(37,99,235,0.10)] blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-[rgba(187,118,33,0.10)] blur-3xl pointer-events-none" />
                        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
                            <div>
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    Headquarters
                                </div>
                                <div className="mt-2 font-display text-xl text-[hsl(var(--text))]">
                                    {BRAND.contact.address}
                                </div>
                            </div>
                            <div>
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    Email
                                </div>
                                <a
                                    href={`mailto:${BRAND.contact.email}`}
                                    className="mt-2 block font-display text-xl text-[hsl(var(--text))] hover:text-[hsl(var(--gold))] transition-colors"
                                >
                                    {BRAND.contact.email}
                                </a>
                            </div>
                            <div>
                                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                    Phone
                                </div>
                                <a
                                    href={`tel:${BRAND.contact.phone.replace(/[^\d+]/g, "")}`}
                                    className="mt-2 block font-display text-xl text-[hsl(var(--text))] hover:text-[hsl(var(--gold))] transition-colors"
                                >
                                    {BRAND.contact.phone}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <FinalCTA onStartProject={onStartProject} />
        </div>
    );
}
